import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * useDocumentDetailTab — wspólna synchronizacja aktywnej zakładki formularza
 * dokumentu z parametrem `?tab=` w URL (T2.1, unifikacja-formatek — silnik
 * formatek, pilot na klasach gołych).
 *
 * Semantyka 1:1 z dotychczasowym per-page blokiem (kopiowanym identycznie na
 * 6 stronach detalu dokumentów — PurchaseInvoiceDetail i pozostałe):
 *  - stan inicjalny czytany z `searchParams.get('tab')`, fallback `defaultTab`.
 *  - efekt synchronizuje URL → state (React Router 6 nie remountuje komponentu
 *    na SPA navigate /new → /:id, więc stan trzeba dociągać z URL ręcznie).
 *  - `setTab(next)` usuwa param `tab` z URL gdy `next === defaultTab` (URL
 *    czysty na domyślnej zakładce), w przeciwnym razie go ustawia — zawsze
 *    przez `{ replace: true }` (bez wpisu w historii per zakładka).
 *
 * Wołanie `useSearchParams()` wewnątrz hooka, gdy strona woła je też na
 * własny użytek (np. `documentTypeId`/`companyId` w trybie create) — to
 * wsparty wzorzec React Router: obie instancje czytają/piszą ten sam
 * kontekst Routera, nie duplikują stanu.
 */
export function useDocumentDetailTab(defaultTab: string = 'data'): {
  tab: string;
  setTab: (next: string) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') ?? defaultTab;
  const [tab, setTabRaw] = useState<string>(initialTab);

  // Sync URL → state (React Router 6 nie remountuje na SPA navigate /new → /:id).
  useEffect(() => {
    const urlTab = searchParams.get('tab') ?? defaultTab;
    if (urlTab !== tab) setTabRaw(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function setTab(next: string) {
    setTabRaw(next);
    const params = new URLSearchParams(searchParams);
    if (next === defaultTab) params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  return { tab, setTab };
}
